/**
 * Helpers for the restaurant edit page while MDS may be unavailable.
 *
 * 'checking': the MDS health check hasn't answered yet. Fields stay locked
 *             (fail closed) but no "MDS unavailable" message is shown.
 * 'down':     the health check says MDS is unavailable.
 * 'up':       the health check says MDS is available.
 */
export type MdsStatus = 'checking' | 'down' | 'up';

export const getMdsStatus = (
  hasChecked: boolean,
  isMDSUpAndRunning: boolean,
): MdsStatus => {
  if (!hasChecked) return 'checking';
  return isMDSUpAndRunning ? 'up' : 'down';
};

export const isMdsLocked = (status: MdsStatus): boolean => status !== 'up';

const RESTAURANT_INFO_FIELDS = [
  'addressLine1',
  'addressLine2',
  'landmark',
  'streetCornerLocation',
  'city',
];

/** Error keys that belong to the address and contact fields (all MDS-owned). */
export const isRestaurantInfoErrorKey = (key: string): boolean =>
  RESTAURANT_INFO_FIELDS.includes(key) ||
  key.startsWith('phone.') ||
  key.startsWith('email.') ||
  key === 'VALIDATION_MIN_PHONE_ERROR' ||
  key === 'VALIDATION_MIN_EMAIL_ERROR';

/**
 * Errors that should stop a save. Errors on locked (read-only) fields are
 * ignored, otherwise bad existing data in a locked field would block saving
 * the fields that are still editable.
 */
export const getBlockingErrors = (
  errors: object,
  status: MdsStatus,
): object => {
  if (!isMdsLocked(status)) return errors;
  return Object.keys(errors)
    .filter(key => !isRestaurantInfoErrorKey(key))
    .reduce((acc, key) => ({ ...acc, [key]: errors[key] }), {});
};

/**
 * Whether the restaurant PATCH (and the phone/email PUTs inside editEntity)
 * has anything to send. True when any `change<field>` flag is set, or when a
 * drive-thru service value that rides along on that PATCH differs from the
 * restaurant's current value.
 */
export const hasRestaurantInfoChanges = (
  restaurantEdit: object,
  serviceValues: { hasDriveThru?: string; hasDigitalDriveThru?: string },
): boolean => {
  const anyFieldChanged = Object.keys(restaurantEdit).some(
    key => key.startsWith('change') && !!restaurantEdit[key],
  );
  const serviceChanged = ['hasDriveThru', 'hasDigitalDriveThru'].some(
    key =>
      typeof serviceValues[key] !== 'undefined' &&
      serviceValues[key] !== restaurantEdit[key],
  );
  return anyFieldChanged || serviceChanged;
};
